import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import {
  Title,
  FromDate,
  Price,
  WrapperLink,
  Typology,
  BillsIncluded,
  PriceBillsWrapper,
  LabelsWrapper,
  HomepageTag
} from './styles';
import MapViewCard from '@spa/shared/components/Search/Card/Mapview';
import ListViewCard from '@spa/shared/components/Search/Card/Listview';
import CloseCard from '@spa/shared/components/Search/Card/Controls/CloseCards';
import Label from '@spa/shared/components/Search/Card/Labels';
import StarFull from '@spa/shared/svg/star-full';
import Gallery from '@spa/shared/components/Search/Card/Gallery';
import { formatDate, formatMoneyPerCountry } from '@spa/shared/utils';
import FeaturesUnitsIcons from '@spa/shared/components/PropertyCard/Info/FeaturesUnitsIcons';
import PropertyPrice from '@spa/shared/components/PropertyCard/Info/PropertyPrice';
import AddToWishListSkeleton from '@spa/shared/components/Search/Card/Controls/AddToWishList/skeleton';
import Tag from '@spa/shared/components/Search/Card/Tags';

const AddToWishList = dynamic(() => import('@spa/shared/components/Search/Card/Controls/AddToWishList'), {
  loading: AddToWishListSkeleton,
  ssr: true
});

const Card = ({
  offerId,
  title,
  price,
  isMobile,
  isMap,
  isLazy,
  isUniresidences,
  isHomepage,
  isChecked,
  isInstantBooking,
  isTrustedLandlord,
  isExclusive,
  isFeatured,
  photos = [],
  tag,
  oldRent,
  maxGuests,
  availableFrom,
  numberOfRooms,
  style,
  handleOfferClick,
  href,
  target,
  sendWishlistClickEvent,
  isBillsIncluded,
  building,
  structuredData = {},
  disableWishlist = false,
  disableGallerySlide = false,
  handleWishlistClick,
  isOfferOnWishlist = false,
  onClose = () => {},
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  language = 'en'
}) => {
  const { t } = useTranslation('translations');
  const Component = isMap ? MapViewCard : ListViewCard;
  const tagComponent = isMap || !tag ? null : <Tag text={tag} key="tag" />;

  const GalleryComponent = (
    <Gallery
      key="Gallery"
      photos={photos}
      disableNavigationArrows={isMobile}
      disableFullNavigation={disableGallerySlide || (isMobile && isMap)}
      isLazy={isLazy}
      title={title}
      isFeatured={isFeatured}
      isUniresidences={isUniresidences}
    />
  );

  const maxGuestsTranslation = maxGuests > 1 ? t('Up to maxGuests people', { maxGuests }) : t('Up to 1 person');

  const TypologyComponent = isMap ? null : (
    <Typology key="Typology">
      {numberOfRooms > 1
        ? `${maxGuestsTranslation} • ${t('count bedrooms', { count: numberOfRooms })}`
        : numberOfRooms === 1
        ? `${maxGuestsTranslation} • ${t('count bedroom', { count: numberOfRooms })}`
        : `${maxGuestsTranslation}`}
    </Typology>
  );

  const isBillsIncludedComponent = isBillsIncluded ? (
    <BillsIncluded key="BillsIncluded">{t('Bills included')}</BillsIncluded>
  ) : null;

  const PriceBillsRowComponent = (
    <PriceBillsWrapper key="Price">
      {isUniresidences ? t('From') : null}
      <Price>
        {oldRent && (
          <span className="old__price">{formatMoneyPerCountry(oldRent.amount, oldRent.currencyCode, language)} </span>
        )}
        <PropertyPrice price={price} isUniresidences={isUniresidences} />
      </Price>
      {isBillsIncludedComponent}
    </PriceBillsWrapper>
  );

  const AvailabilityComponent =
    !isUniresidences && !isHomepage ? (
      <FromDate key="FromDate">
        {t('From')} {formatDate(availableFrom, language, 'dd MMM yyyy')}
      </FromDate>
    ) : null;

  const FeaturesComponent = isUniresidences && !isMap ? <FeaturesUnitsIcons building={building} /> : null;

  const HomepageLabelsComponent = isHomepage ? (
    <LabelsWrapper>
      {isChecked ? <HomepageTag>{t('global.labels.verified')}</HomepageTag> : null}
      {isInstantBooking ? <HomepageTag>{t('global.labels.instant_booking')}</HomepageTag> : null}
      {isTrustedLandlord ? <HomepageTag>{t('global.labels.trusted')} </HomepageTag> : null}
      {isExclusive ? <HomepageTag>{t('global.labels.has-exclusive')}</HomepageTag> : null}
    </LabelsWrapper>
  ) : null;

  const TitleComponent = (
    <Title href={href} target={target} rel="noreferrer">
      {isHomepage ? <p>{title}</p> : <h2 key="Title">{title}</h2>}
    </Title>
  );

  return (
    <WrapperLink
      isMobile={isMobile}
      isMap={isMap}
      isFeatured={isFeatured}
      style={style}
      onClick={handleOfferClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      itemProp="itemListElement"
      itemType="https://schema.org/Offer"
      itemScope
    >
      {Object.keys(structuredData).map(key => (
        <meta key={key} itemProp={key} content={structuredData[key]} />
      ))}
      <Component
        isMobile={isMobile}
        isMap={isMap}
        media={[tagComponent, GalleryComponent]}
        isUniresidences={isUniresidences}
        isHomepage={isHomepage}
        description={[
          TypologyComponent,
          TitleComponent,
          HomepageLabelsComponent,
          FeaturesComponent,
          AvailabilityComponent,
          PriceBillsRowComponent
        ]}
      />
      {isMap && <CloseCard onClick={onClose} />}
      {isMap && isFeatured && (
        <Label isFeatured={true} isUniresidences={false}>
          <StarFull />
          {t('Featured')}
        </Label>
      )}
      {!isHomepage && !disableWishlist ? (
        <AddToWishList
          offerId={offerId}
          sendWishlistClickEvent={sendWishlistClickEvent}
          handleWishlistClick={handleWishlistClick}
          isOfferOnWishlist={isOfferOnWishlist}
        />
      ) : null}
    </WrapperLink>
  );
};

Card.propTypes = {
  offerId: PropTypes.string,
  isMobile: PropTypes.bool,
  isMap: PropTypes.bool,
  isLazy: PropTypes.bool,
  title: PropTypes.string,
  price: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    currencyCode: PropTypes.string.isRequired
  }),
  oldRent: PropTypes.object,
  link: PropTypes.string,
  photos: PropTypes.array,
  label: PropTypes.string,
  tag: PropTypes.string,
  isUniresidences: PropTypes.bool,
  isHomepage: PropTypes.bool,
  isChecked: PropTypes.bool,
  isInstantBooking: PropTypes.bool,
  isTrustedLandlord: PropTypes.bool,
  isExclusive: PropTypes.bool,
  isFeatured: PropTypes.bool,
  sendWishlistClickEvent: PropTypes.func,
  maxGuests: PropTypes.number,
  availableFrom: PropTypes.instanceOf(Date),
  numberOfRooms: PropTypes.number,
  style: PropTypes.object,
  handleOfferClick: PropTypes.func,
  href: PropTypes.string,
  target: PropTypes.string,
  isBillsIncluded: PropTypes.bool,
  building: PropTypes.object,
  onClose: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  language: PropTypes.string,
  disableWishlist: PropTypes.bool,
  disableGallerySlide: PropTypes.bool,
  handleWishlistClick: PropTypes.func,
  isOfferOnWishlist: PropTypes.bool,
  structuredData: PropTypes.object
};

export default React.memo(Card);
